import { useState, useEffect, useMemo } from 'react';
import { useCart } from './CartContext';

const SUPABASE_FUNCTIONS_URL = 'https://fyxrbfcnzlrjjbubjzth.supabase.co/functions/v1';

const SHOPIFY_PRODUCT_IDS = [
  'gid://shopify/Product/10307808952609',
  'gid://shopify/Product/10307809050913',
  'gid://shopify/Product/10307809476897',
  'gid://shopify/Product/10307809607969',
];

interface ShopifyVariant {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  compareAtPrice: { amount: string; currencyCode: string } | null;
  availableForSale: boolean;
  quantityAvailable: number;
  selectedOptions: { name: string; value: string }[];
}

interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  descriptionHtml: string;
  handle: string;
  featuredImage: { url: string; altText: string } | null;
  images: { edges: { node: { url: string; altText: string } }[] };
  variants: { edges: { node: ShopifyVariant }[] };
  priceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
    maxVariantPrice: { amount: string; currencyCode: string };
  };
  compareAtPriceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
  };
}

export default function ProductSection() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('Small');
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/shopify-products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getProducts', productIds: SHOPIFY_PRODUCT_IDS }),
        });
        const data = await res.json();
        const nodes: ShopifyProduct[] = (data?.data?.nodes ?? []).filter(Boolean);
        setProducts(nodes);
        if (nodes.length > 0) {
          setSelectedProduct(nodes[0]);
        }
      } catch {
        setError('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const getVariantForSize = (product: ShopifyProduct, size: string): ShopifyVariant | undefined =>
    product.variants.edges
      .map((e) => e.node)
      .find((v) =>
        v.selectedOptions.some(
          (o) => o.name.toLowerCase() === 'size' && o.value.toLowerCase() === size.toLowerCase()
        )
      );

  const availableSizes = useMemo(() => {
    if (!selectedProduct) return ['Small', 'Large'];
    const sizes = new Set<string>();
    selectedProduct.variants.edges.forEach((e) => {
      const sizeOpt = e.node.selectedOptions.find((o) => o.name.toLowerCase() === 'size');
      if (sizeOpt) sizes.add(sizeOpt.value);
    });
    return sizes.size > 0 ? Array.from(sizes) : ['Small', 'Large'];
  }, [selectedProduct]);

  const selectedVariant = useMemo(() => {
    if (!selectedProduct) return null;
    return getVariantForSize(selectedProduct, selectedSize) ?? selectedProduct.variants.edges[0]?.node ?? null;
  }, [selectedProduct, selectedSize]);

  const price = selectedVariant ? parseFloat(selectedVariant.price.amount) : 0;
  // Compare-at: Small $18→$23 (+$5), Large $22→$27 (+$5)
  const compareAt = selectedVariant ? price + 5 : null;

  const handleAddToCart = () => {
    if (!selectedVariant || !selectedProduct) return;
    const imgUrl = selectedProduct.featuredImage?.url ?? selectedProduct.images.edges[0]?.node.url ?? '';
    addItem({
      variantId: selectedVariant.id,
      productTitle: selectedProduct.title,
      variantTitle: selectedSize,
      price,
      quantity,
      imageUrl: imgUrl,
      source: 'scooper',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const formatPrice = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

  return (
    <section id="product" className="py-24 md:py-32 bg-[#F8F6F3]">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-16">
          <span className="text-[#8A9B85] text-xs font-bold uppercase tracking-widest">Choose Your Style</span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Four Designs, One Perfect Fit
          </h2>
          <p className="text-stone-500 text-lg">
            Starting at — <strong className="text-[#C4956A]">{formatPrice(price || 18)}</strong> &nbsp;·&nbsp; Free shipping over $50
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Style Selector */}
          <div className="w-full lg:w-1/2" data-product-shop>
            <p className="text-stone-700 font-semibold text-sm mb-4">Pick Your Style</p>

            {loading ? (
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border-2 border-stone-200 animate-pulse">
                    <div className="bg-stone-200 aspect-square"></div>
                    <div className="p-3 bg-white">
                      <div className="h-4 bg-stone-200 rounded mb-2"></div>
                      <div className="h-3 bg-stone-100 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12 text-stone-500">
                <i className="ri-error-warning-line text-3xl mb-2 block text-[#C4956A]"></i>
                <p>{error}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {products.map((product) => {
                  const isSelected = selectedProduct?.id === product.id;
                  const imgUrl = product.featuredImage?.url ?? product.images.edges[0]?.node.url ?? '';
                  const variant = getVariantForSize(product, selectedSize) ?? product.variants.edges[0]?.node;
                  const pPrice = variant ? parseFloat(variant.price.amount) : 0;
                  // Compare-at: Small $18→$23 (+$5), Large $22→$27 (+$5)
                  const pCompareAt = pPrice > 0 ? pPrice + 5 : null;

                  return (
                    <div
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-200 cursor-pointer select-none ${
                        isSelected ? 'border-[#C4956A]' : 'border-stone-200 hover:border-[#C4956A]/50'
                      }`}
                    >
                      <div className="bg-white aspect-square">
                        <img src={imgUrl} alt={product.title} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-3 bg-white">
                        <p className="text-stone-800 font-semibold text-sm">{product.title}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-[#C4956A] text-xs font-bold">{formatPrice(pPrice)}</p>
                          {pCompareAt && pCompareAt > pPrice && (
                            <p className="text-stone-400 text-xs line-through">{formatPrice(pCompareAt)}</p>
                          )}
                        </div>
                        <p className="text-stone-400 text-xs mt-0.5 leading-snug line-clamp-2">{product.description}</p>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-[#C4956A] rounded-full">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Purchase Panel */}
          <div className="w-full lg:w-1/2 lg:sticky lg:top-24">
            <div className="bg-white rounded-3xl p-8 border border-stone-100">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-2xl font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {selectedProduct?.title ?? 'Select a Style'}
                </h3>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-[#C4956A] text-sm"></i>
                  ))}
                </div>
              </div>
              <p className="text-stone-500 text-sm mb-4 leading-relaxed">
                {selectedProduct?.description ?? 'Choose a style from the left to get started.'}
              </p>

              <div className="flex items-center gap-2 bg-[#F4F7F4] border border-[#8A9B85]/30 rounded-xl px-4 py-2.5 mb-6 w-fit">
                <i className="ri-gift-line text-[#8A9B85] text-base"></i>
                <span className="text-[#5a7055] text-xs font-semibold">Includes 1 plastic bag roll — ready to use right out of the box</span>
              </div>

              {/* Size Selector */}
              <div className="mb-6">
                <p className="text-stone-700 font-semibold text-sm mb-3">Select Size</p>
                <div className="grid grid-cols-2 gap-3">
                  {availableSizes.map((size) => {
                    const isActive = selectedSize === size;
                    return (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                          isActive ? 'border-[#8A9B85] bg-[#F4F7F4]' : 'border-stone-200 bg-white hover:border-stone-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-stone-800 font-bold text-sm">{size}</span>
                          {isActive && (
                            <div className="w-4 h-4 flex items-center justify-center bg-[#8A9B85] rounded-full">
                              <i className="ri-check-line text-white text-[10px]"></i>
                            </div>
                          )}
                        </div>
                        <p className="text-stone-500 text-xs">
                          {size === 'Small' ? 'For dogs under 50 lbs (22kg)' : 'For dogs 50 lbs (22kg) and up'}
                        </p>
                        <p className="text-[#8A9B85] text-xs font-medium mt-1">
                          {size === 'Small' ? 'Compact & lightweight' : 'Extra capacity'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <p className="text-stone-700 font-semibold text-sm mb-3">Quantity</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-10 text-center text-stone-800 font-semibold text-sm">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <div>
                    {compareAt && compareAt > price && (
                      <span className="text-stone-400 text-sm line-through mr-2">{formatPrice(compareAt * quantity)}</span>
                    )}
                    <span className="text-2xl font-bold text-stone-800">{formatPrice(price * quantity)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={!selectedVariant || loading}
                className={`w-full py-4 rounded-full font-bold text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  added
                    ? 'bg-[#8A9B85] text-white'
                    : !selectedVariant || loading
                    ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                    : 'bg-[#C4956A] hover:bg-[#b5845a] text-white'
                }`}
              >
                {added ? (
                  <><i className="ri-check-line text-lg"></i> Added to Cart!</>
                ) : (
                  <><i className="ri-shopping-bag-line text-lg"></i> Add to Cart — {formatPrice(price * quantity)}</>
                )}
              </button>

              <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-stone-100">
                <div className="flex items-center gap-1.5 text-stone-500 text-xs">
                  <i className="ri-truck-line text-[#8A9B85]"></i>
                  Free shipping $50+
                </div>
                <div className="flex items-center gap-1.5 text-stone-500 text-xs">
                  <i className="ri-refresh-line text-[#8A9B85]"></i>
                  30-day returns
                </div>
                <div className="flex items-center gap-1.5 text-stone-500 text-xs">
                  <i className="ri-shield-check-line text-[#8A9B85]"></i>
                  Secure checkout
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
